import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from './AuthContext';
import UsageDashboard from './UsageDashboard';

const Profile = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(false);

  const getBackendUrl = () => {
    return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  };

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }

    const fetchSubscriptionData = async () => {
      try {
        const response = await fetch(`${getBackendUrl()}/api/subscription/current`, {
          credentials: 'include',
        });
        
        if (response.ok) {
          const data = await response.json();
          setSubscriptionData(data);
        }
      } catch (error) {
        console.error('Error fetching subscription data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionData();
  }, [user, navigate]);

  const handleCancelSubscription = async () => {
    if (!subscriptionData || subscriptionData.subscription.subscription_name === 'Explorer') {
      alert('You are on the free plan. No subscription to cancel.');
      return;
    }

    const confirmCancel = window.confirm(
      'Are you sure you want to cancel your subscription? It will remain active until the end of your current billing period.'
    );

    if (!confirmCancel) return;

    setCancelLoading(true);
    try {
      const response = await fetch(`${getBackendUrl()}/api/subscription/cancel`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        alert(data.message);
        // Refresh subscription data
        window.location.reload();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert('Error cancelling subscription. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleUpgrade = () => {
    navigate('/');
    // Scroll to pricing section
    setTimeout(() => {
      const pricingSection = document.querySelector('#pricing') || document.querySelector('[data-section="pricing"]');
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const handleLogout = async () => {
    const confirmLogout = window.confirm('Are you sure you want to log out?');
    if (confirmLogout) {
      try {
        await logout();
        navigate('/');
      } catch (error) {
        console.error('Error logging out:', error);
        navigate('/');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-400 mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/')}
                className="text-green-400 hover:text-green-300 transition-colors"
              >
                ← Back to Dashboard
              </button>
            </div>
            <h1 className="text-2xl font-bold">Profile Settings</h1>
            <div className="w-32"></div> {/* Spacer for centering */}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* User Information */}
          <div className="lg:col-span-1">
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-bold mb-6">User Information</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Name
                  </label>
                  <div className="bg-gray-700 rounded-lg p-3">
                    <p className="text-white">{user.name}</p>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Email
                  </label>
                  <div className="bg-gray-700 rounded-lg p-3">
                    <p className="text-white">{user.email}</p>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Account Type
                  </label>
                  <div className="bg-gray-700 rounded-lg p-3">
                    <p className="text-white">
                      {user.is_google_user ? 'Google Account' : 'Standard Account'}
                    </p>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Member Since
                  </label>
                  <div className="bg-gray-700 rounded-lg p-3">
                    <p className="text-white">
                      {new Date(user.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Account Actions */}
              <div className="mt-8 space-y-4">
                <button
                  onClick={handleLogout}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-4 rounded-lg transition duration-300"
                >
                  Log Out
                </button>
              </div>
            </div>
          </div>

          {/* Usage Dashboard and Subscription Management */}
          <div className="lg:col-span-2 space-y-8">
            {/* Usage Dashboard */}
            <UsageDashboard />

            {/* Subscription Management */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-bold mb-6">Subscription Management</h2>
              
              {subscriptionData ? (
                <div className="space-y-6">
                  {/* Current Subscription */}
                  <div className="bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">Current Plan</h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        subscriptionData.subscription.subscription_name === 'Explorer' ? 'bg-blue-900 text-blue-200' :
                        subscriptionData.subscription.subscription_name === 'Navigator' ? 'bg-green-900 text-green-200' :
                        subscriptionData.subscription.subscription_name === 'Advocate' ? 'bg-purple-900 text-purple-200' :
                        'bg-yellow-900 text-yellow-200'
                      }`}>
                        {subscriptionData.subscription.subscription_name}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-400">Status</p>
                        <p className="text-white capitalize">{subscriptionData.subscription.status}</p>
                      </div>
                      
                      {subscriptionData.subscription.current_period_end && (
                        <div>
                          <p className="text-gray-400">
                            {subscriptionData.subscription.cancel_at_period_end ? 'Expires' : 'Next Billing'}
                          </p>
                          <p className="text-white">
                            {new Date(subscriptionData.subscription.current_period_end).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {subscriptionData.subscription.cancel_at_period_end && (
                      <div className="mt-4 p-3 bg-yellow-900/20 border border-yellow-700 rounded-lg">
                        <p className="text-yellow-200 text-sm">
                          ⚠️ Your subscription is set to cancel at the end of the current period.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Plan Features */}
                  <div className="bg-gray-700 rounded-lg p-4">
                    <h3 className="text-lg font-semibold mb-3">Plan Features</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-400">AI Interactions</p>
                        <p className="text-white">
                          {subscriptionData.package_config?.features?.unlimited_interactions 
                            ? 'Unlimited' 
                            : `${subscriptionData.package_config?.features?.ai_interactions || 0} per month`
                          }
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-gray-400">Document Uploads</p>
                        <p className="text-white">
                          {subscriptionData.package_config?.features?.unlimited_uploads 
                            ? 'Unlimited' 
                            : `${subscriptionData.package_config?.features?.document_uploads || 0} per month`
                          }
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-gray-400">Document Scans</p>
                        <p className="text-white">
                          {subscriptionData.package_config?.features?.unlimited_scans 
                            ? 'Unlimited' 
                            : `${subscriptionData.package_config?.features?.document_scans || 0} per month`
                          }
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-gray-400">AI Agents</p>
                        <p className="text-white">
                          {subscriptionData.package_config?.features?.unlimited_agents 
                            ? 'All 8 Agents' 
                            : `${subscriptionData.package_config?.features?.ai_agents || 0} agent${subscriptionData.package_config?.features?.ai_agents > 1 ? 's' : ''}`
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    <button
                      onClick={handleUpgrade}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg transition duration-300"
                    >
                      {subscriptionData.subscription.subscription_name === 'Explorer' ? 'Upgrade Plan' : 'Change Plan'}
                    </button>
                    
                    {subscriptionData.subscription.subscription_name !== 'Explorer' && (
                      <button
                        onClick={handleCancelSubscription}
                        disabled={cancelLoading}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-4 rounded-lg transition duration-300 disabled:opacity-50"
                      >
                        {cancelLoading ? 'Cancelling...' : 'Cancel Subscription'}
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-400">Unable to load subscription data.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;